import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Api as ApiIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { WalteroTypography, WalteroCard } from '../waltero';

/**
 * API Usage Dashboard Component
 * Displays summary statistics and key metrics
 */
const ApiUsageDashboard = ({ data, period, onUserClick, onEndpointClick }) => {
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <WalteroTypography variant="body1" color="textSecondary">
          Loading dashboard data...
        </WalteroTypography>
      </Box>
    );
  }

  const { summary, current, growth, topUsers, topEndpoints } = data;

  // Format number with commas
  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  // Get trend icon
  const getTrendIcon = (value) => {
    if (value > 0) return <TrendingUpIcon fontSize="small" color="success" />;
    if (value < 0) return <TrendingDownIcon fontSize="small" color="error" />;
    return null;
  };

  // Get trend color
  const getTrendColor = (value, inverse = false) => {
    if (inverse) {
      if (value > 0) return 'error.main';
      if (value < 0) return 'success.main';
    } else {
      if (value > 0) return 'success.main';
      if (value < 0) return 'error.main';
    }
    return 'text.secondary';
  };

  // Summary cards configuration
  const summaryCards = [
    {
      title: 'Total API Calls',
      value: formatNumber(summary?.totalApiCalls || 0),
      growth: growth?.calls,
      icon: <ApiIcon />,
      color: '#094872'
    },
    {
      title: 'Active Users',
      value: formatNumber(summary?.activeUsers || 0),
      growth: growth?.users,
      icon: <GroupIcon />,
      color: '#c35d2c'
    },
    {
      title: 'Success Rate',
      value: `${summary?.successRate || 0}%`,
      growth: growth?.success_rate,
      icon: <CheckCircleIcon />,
      color: '#4CAF50'
    },
    {
      title: 'Avg Response Time',
      value: `${summary?.averageResponseTime || 0}ms`,
      growth: growth?.response_time,
      icon: <SpeedIcon />,
      color: '#FF9800',
      inverseGrowth: true
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={3}>
        {/* Summary Cards */}
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <WalteroCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: card.color, mr: 2 }}>
                    {card.icon}
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <WalteroTypography variant="body2" color="textSecondary">
                      {card.title}
                    </WalteroTypography>
                    <WalteroTypography variant="h5" fontWeight="bold">
                      {card.value}
                    </WalteroTypography>
                  </Box>
                </Box>
                {card.growth !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {getTrendIcon(card.growth)}
                    <WalteroTypography
                      variant="body2"
                      sx={{
                        ml: 0.5,
                        color: getTrendColor(card.growth, card.inverseGrowth)
                      }}
                    >
                      {Math.abs(card.growth)}% from last period
                    </WalteroTypography>
                  </Box>
                )}
              </CardContent>
            </WalteroCard>
          </Grid>
        ))}

        {/* Top Users */}
        <Grid item xs={12} md={6}>
          <WalteroCard>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>
                Top API Users
              </WalteroTypography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {topUsers && topUsers.length > 0 ? (
                  topUsers.map((user, index) => (
                    <ListItem
                      key={user.userId}
                      button
                      onClick={() => onUserClick(user.userId)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#094872' }}>
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name || user.email}
                        secondary={
                          <Box>
                            <WalteroTypography variant="caption" display="block">
                              {user.email}
                            </WalteroTypography>
                            <WalteroTypography variant="caption" color="primary">
                              {formatNumber(user.total_calls)} calls
                            </WalteroTypography>
                          </Box>
                        }
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <Chip
                          label={`${user.error_rate}% errors`}
                          size="small"
                          color={user.error_rate > 5 ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </Box>
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No data available"
                      secondary="No users have made API calls in this period"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </WalteroCard>
        </Grid>

        {/* Top Endpoints */}
        <Grid item xs={12} md={6}>
          <WalteroCard>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>
                Most Used Endpoints
              </WalteroTypography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {topEndpoints && topEndpoints.length > 0 ? (
                  topEndpoints.map((endpoint, index) => (
                    <ListItem
                      key={endpoint.endpoint}
                      button
                      onClick={() => onEndpointClick(endpoint.endpoint)}
                      sx={{
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                              label={endpoint.endpoint.split('#')[1] || 'GET'}
                              size="small"
                              color="primary"
                              sx={{ mr: 1 }}
                            />
                            <WalteroTypography variant="body2">
                              {endpoint.endpoint.split('#')[0]}
                            </WalteroTypography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <WalteroTypography variant="caption" sx={{ minWidth: 100 }}>
                                Total Calls:
                              </WalteroTypography>
                              <WalteroTypography variant="caption" fontWeight="bold">
                                {formatNumber(endpoint.total_calls)}
                              </WalteroTypography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <WalteroTypography variant="caption" sx={{ minWidth: 100 }}>
                                Avg Response:
                              </WalteroTypography>
                              <WalteroTypography variant="caption" fontWeight="bold">
                                {endpoint.average_response_time}ms
                              </WalteroTypography>
                            </Box>
                          </Box>
                        }
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <WalteroTypography variant="caption" display="block" color="textSecondary">
                          {endpoint.unique_users} users
                        </WalteroTypography>
                        <Chip
                          label={`${endpoint.error_rate}% errors`}
                          size="small"
                          color={endpoint.error_rate > 5 ? 'error' : 'success'}
                          variant="outlined"
                        />
                      </Box>
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No data available"
                      secondary="No endpoints have been called in this period"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </WalteroCard>
        </Grid>

        {/* Performance Overview */}
        <Grid item xs={12}>
          <WalteroCard>
            <CardContent>
              <WalteroTypography variant="h6" gutterBottom>
                Performance Overview
              </WalteroTypography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <WalteroTypography variant="body2">Success Rate</WalteroTypography>
                      <WalteroTypography variant="body2" fontWeight="bold">
                        {current?.success_rate || 0}%
                      </WalteroTypography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={current?.success_rate || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: current?.success_rate >= 95 ? 'success.main' : current?.success_rate >= 90 ? 'warning.main' : 'error.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <WalteroTypography variant="body2">Error Rate</WalteroTypography>
                      <WalteroTypography variant="body2" fontWeight="bold">
                        {current?.error_rate || 0}%
                      </WalteroTypography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={current?.error_rate || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: current?.error_rate <= 5 ? 'success.main' : current?.error_rate <= 10 ? 'warning.main' : 'error.main'
                        }
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={6} sm={3}>
                  <WalteroTypography variant="caption" color="textSecondary">
                    Total Endpoints
                  </WalteroTypography>
                  <WalteroTypography variant="h6">
                    {current?.unique_endpoints || 0}
                  </WalteroTypography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <WalteroTypography variant="caption" color="textSecondary">
                    Successful Calls
                  </WalteroTypography>
                  <WalteroTypography variant="h6">
                    {formatNumber(current?.successful_calls || 0)}
                  </WalteroTypography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <WalteroTypography variant="caption" color="textSecondary">
                    Failed Calls
                  </WalteroTypography>
                  <WalteroTypography variant="h6" color="error">
                    {formatNumber(current?.failed_calls || 0)}
                  </WalteroTypography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <WalteroTypography variant="caption" color="textSecondary">
                    Avg Response Time
                  </WalteroTypography>
                  <WalteroTypography variant="h6">
                    {current?.average_response_time || 0}ms
                  </WalteroTypography>
                </Grid>
              </Grid>
            </CardContent>
          </WalteroCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ApiUsageDashboard;